export default function AtcCalendarService(
    $stateParams,
    $timeout,
    $filter,
    atcTimeService,
    l10nService,
    localStorageService,
    userService,
    MOMENT,
) {
    'ngInject';

    const CALENDAR_SEARCH_CONDITIONS = 'atc-calendar-search-conditions';
    const DEPARTURE_PROPERTY = 'Departure';
    const RETURN_PROPERTY = 'Return';
    const PRICE_PROPERTY = 'TotalChargeDifference';
    const CURRENCY_CODE_PROPERTY = 'CurrencyCode';
    const CURRENCY_SYMBOL_PROPERTY = 'CurrencySymbol';
    const INDEX_PROPERTY = 'Index';

    const CALENDAR_DATE_FORMAT = 'ddd, MMM DD';
    const API_DATE_FORMAT = 'DDMMYY';

    return {
        prepareScopeModel,
        areRoundTrips,
        convertTravelDate,
        getBestPrice,
        tagBestPrice,
        getDepartureDates,
        getReturnDates,
        getTravelPrice,
        getPriceDescription,
        getFlightFromDates,
        prepareCalendarResponse,
        addDaysToRequestSegments,
        cleanCalendarSearchConditions,
        getCalendarFlightsRequest,
        getFindFlightsRequest,
        animateResults,

        // demo methods
        generateRoundTripResults,
        generateOneWayTripResults,
    };

    function prepareScopeModel(model, data) {
        // get real flights
        model.flights = prepareCalendarResponse(data);

        // get demo flights
        // model.flights = generateRoundTripResults();
        // model.flights = generateOneWayTripResults();

        model.roundtrips = areRoundTrips(model.flights);
        model.bestPrice = getBestPrice(model.flights);
        tagBestPrice(model.flights, model.bestPrice);
        model.departures = getDepartureDates(model.flights);
        model.returns = model.roundtrips ? getReturnDates(model.flights) : [];
    }

    function cleanCalendarSearchConditions() {
        localStorageService.set(CALENDAR_SEARCH_CONDITIONS, null);
    }

    function getCalendarFlightsRequest() {
        let req = localStorageService.get(CALENDAR_SEARCH_CONDITIONS);
        if (!req) {
            req = angular.copy($stateParams.searchConditions);
            localStorageService.set(CALENDAR_SEARCH_CONDITIONS, req);
            return req;
        }
        $stateParams.searchConditions = req;
        return req;
    }

    function getFindFlightsRequest(baseRequest, flight) {
        baseRequest.Segments = flight.Sectors.map((segment, index) => {
            const departure = atcTimeService.formatDate(segment.Legs[0].DateOfDeparture, API_DATE_FORMAT);
            return {
                departure: [{Code: segment.Legs[0].AirportOfDeparture, IsCity: false}],
                arrival: [{Code: segment.Legs[segment.Legs.length - 1].AirportOfArrival, IsCity: false}],
                date: departure,
                cabin: segment.Legs[0].Cabin,
                carriers: baseRequest.Segments[index].carriers,
                directFlights: baseRequest.Segments[index].directFlights,
                tattoos: baseRequest.Segments[index].tattoos,
                isChanged: baseRequest.Segments[index].isChanged || departure !== baseRequest.Segments[index].date,
            };
        });

        return baseRequest;
    }

    function prepareCalendarResponse(flights) {
        flights.forEach((flight, i) => {
            flight[DEPARTURE_PROPERTY] = !flight.Sectors || !flight.Sectors.length ? null : new Date(flight.Sectors[0].Legs[0].DateTimeOfDeparture);
            flight[RETURN_PROPERTY] = !flight.Sectors || flight.Sectors.length < 2 ? null : new Date(flight.Sectors[flight.Sectors.length - 1].Legs[0].DateTimeOfDeparture);
            // flight[PRICE_PROPERTY] = flight.Price;
            flight[CURRENCY_CODE_PROPERTY] = userService.getRegionalSettings().currency.toUpperCase();
            flight[CURRENCY_SYMBOL_PROPERTY] = userService.getCurrencySign();
            flight[INDEX_PROPERTY] = i;
        });
        return flights;
    }

    function areRoundTrips(flights) {
        return flights.filter((flight) => flight[DEPARTURE_PROPERTY] && flight[RETURN_PROPERTY]).length > 0;
    }

    function getBestPrice(flights) {
        const results = flights.slice();
        results.sort((res1, res2) => +res1[PRICE_PROPERTY] - +res2[PRICE_PROPERTY]);
        return +results[0][PRICE_PROPERTY];
    }

    function tagBestPrice(flights, bestPrice) {
        for (let i = 0; i < flights.length; i++) {
            flights[i].IsBestPrice = flights[i][PRICE_PROPERTY].toFixed(2) === bestPrice.toFixed(2);
        }
    }

    function getDepartureDates(flights) {
        return getDates(flights, DEPARTURE_PROPERTY);
    }

    function getReturnDates(flights) {
        return getDates(flights, RETURN_PROPERTY);
    }

    function getDates(flights, prop) {
        const dates = [];
        for (let i = 0; i < flights.length; i++) {
            if (dates.filter((date) => atcTimeService.isSameDay(date, flights[i][prop])).length === 0) {
                dates.push(atcTimeService.getDateNoTime(flights[i][prop]));
            }
        }
        return dates.sort((date1, date2) => date1 - date2);
    }

    function convertTravelDate(date) {
        // TODO: change this
        //const {dateFormat} = userService.getRegionalSettings();
        const dateFormat = CALENDAR_DATE_FORMAT;
        return date ? MOMENT.format(date, dateFormat) : '';
    }

    function getTravelPrice(flight) {
        return !flight[PRICE_PROPERTY] && flight[PRICE_PROPERTY] !== 0 ? '' : `${userService.getCurrencySign()} ${flight[PRICE_PROPERTY].toFixed(2)}`;
    }

    function getPriceDescription(flight) {
        const currSign = userService.getCurrencySign();
        const displayBalance = flight.IsRefundable ? flight.FareBalance : (flight.FareBalance + flight.MarkDifference + flight.ResidualValue);
        return `<div><table class="fare-price-details"><tbody>
            <tr><td>${l10nService.localize('Penalty')}:</td><td class="text-right">${$filter('atcCurrencyByCode')(flight.Penalty, currSign)}</td></tr>
            <tr><td>${l10nService.localize('Tax Difference')}:</td><td class="text-right">${$filter('atcCurrencyByCode')(flight.TaxDifference, currSign)}</td></tr>
            <tr><td>${l10nService.localize('Fare Balance')}:</td><td class="text-right">${$filter('atcCurrencyByCode')(displayBalance, currSign)}</td></tr>
            ${(flight.HandlingFee === 0 ? '' : `<tr><td>${l10nService.localize('Handling Fee')}:</td><td class="text-right">${$filter('atcCurrencyByCode')(flight.HandlingFee, currSign)}</td></tr>`)}
            <tr><td>${l10nService.localize('Charge Difference')}:</td><td class="text-right">${$filter('atcCurrencyByCode')(flight.TotalChargeDifference, currSign)}</td></tr>
            </tbody></table></div>`;
    }

    function getFlightFromDates(flights, departureDate, returnDate) {
        const results = flights.filter((flight) =>
            (!flight[DEPARTURE_PROPERTY] || atcTimeService.isSameDay(flight[DEPARTURE_PROPERTY], departureDate))
            && (!flight[RETURN_PROPERTY] || atcTimeService.isSameDay(flight[RETURN_PROPERTY], returnDate)));
        return results[0];
    }

    function addDaysToRequestSegments(segments, days) {
        segments.forEach((seg) => {
            const segmDate = atcTimeService.parse(seg.date, API_DATE_FORMAT);
            const newDate = atcTimeService.addDays(segmDate, days);
            seg.date = atcTimeService.parseAndFormat(newDate, null, API_DATE_FORMAT);
        });
    }

    function animateResults(flights) {
        $timeout(function() {
            if (!$('.atc-calendar-prices .price-container').length) {
                return;
            }
            const priceGroups = [];
            function updateGroups(index) {
                const flight = flights[index];
                const price = getTravelPrice(flight);
                const group = priceGroups.filter((prGroup) => prGroup.price === price)[0];
                if (!group) {
                    priceGroups.push({price, indexArr: [flight[INDEX_PROPERTY]]});
                } else {
                    group.indexArr.push(flight[INDEX_PROPERTY]);
                }
            }
            for (let index = 0; index < flights.length; index++) {
                updateGroups(index);
            }
            for (let index = 0; index < priceGroups.length; index++) {
                const group = priceGroups[index];
                const $elements = $(group.indexArr.map((item) => '.atc-calendar-prices .price-container[data-findex="' + item + '"]').join(','));
                setTimeout(function() {
                    $elements.fadeIn();
                }, index * 500);
            }
        });
    }

    // fake api results
    function generateRoundTripResults() {
        const minPrice = 500;
        const maxPrice = 750;
        // const basePrice = 625;
        const baseDeparture = atcTimeService.addDays(new Date(), 7);
        const baseReturn = atcTimeService.addDays(new Date(), 10);

        const res = [];
        let counter = 0;
        for (let i = 0; i < 7; i++) {
            for (let j = 0; j < 7; j++) {
                const randomPrice = Math.random() * (maxPrice - minPrice) + minPrice;
                const dep = atcTimeService.addDays(baseDeparture, -3 + i);
                const ret = atcTimeService.addDays(baseReturn, -3 + j);

                if (dep < ret) {
                    const flight = {};
                    flight[INDEX_PROPERTY] = counter++;
                    flight[DEPARTURE_PROPERTY] = dep;
                    flight[RETURN_PROPERTY] = ret;
                    flight[PRICE_PROPERTY] = +randomPrice.toFixed(2);
                    flight[CURRENCY_CODE_PROPERTY] = userService.getRegionalSettings().currency.toUpperCase();
                    flight[CURRENCY_SYMBOL_PROPERTY] = userService.getCurrencySign();
                    res.push(flight);
                }
           }
        }
        return res;
    }

    // fake api results
    function generateOneWayTripResults() {
        const minPrice = 500;
        const maxPrice = 750;
        // const basePrice = 625;
        const baseDeparture = atcTimeService.addDays(new Date(), 7);

        const res = [];
        let counter = 0;
        for (let i = 0; i < 7; i++) {
            const randomPrice = Math.random() * (maxPrice - minPrice) + minPrice;
            const dep = atcTimeService.addDays(baseDeparture, -3 + i);

            const flight = {};
            flight[INDEX_PROPERTY] = counter++;
            flight[DEPARTURE_PROPERTY] = dep;
            flight[RETURN_PROPERTY] = null;
            flight[PRICE_PROPERTY] = +randomPrice.toFixed(2);
            flight[CURRENCY_CODE_PROPERTY] = userService.getRegionalSettings().currency.toUpperCase();
            flight[CURRENCY_SYMBOL_PROPERTY] = userService.getCurrencySign();
            res.push(flight);
        }
        return res;
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/atc/services/atcCalendarService.js